import { Button, Divider, Flex, List, Popover, Table, Tag } from 'antd'
import { TableColumnsType } from 'antd/lib'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useStocksServiceGetIssuanceListEndpoint } from 'src/api/queries'
import {
  InventoryDocumentItemResponse,
  InventoryDocumentState
} from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { IssuanceFilters } from '../components'
import { SelectedItemsState } from '../types'
import { getStatus } from '../utils'

dayjs.extend(customParseFormat)

const VISIBLE_TAG_COUNT = 2

const MapValuesToIds = (data: SelectedItemsState) => {
  return {
    legalEntityIds: data?.legalEntityIds?.values,
    fromDate: data?.inDate?.values?.[0],
    toDate: data?.inDate?.values?.[1],
    searchTerm: data?.search?.values?.[0],
    states: data?.states?.values as InventoryDocumentState[]
  }
}

export const IssuancesPage = () => {
  const [selectedItems, setSelectedItems] = useState<SelectedItemsState>({})

  const navigate = useNavigate()
  const { t } = useLocales()
  const pagination = useTablePagination()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...MapValuesToIds(selectedItems)
  }

  const { data, isLoading } = useStocksServiceGetIssuanceListEndpoint(
    queryParams,
    [queryParams]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'documentNumber',
        title: t('stock.accompanying_invoice'),
        dataIndex: 'documentNumber'
      },
      {
        key: 'documentDate',
        title: t('stock_administration.release_date'),
        render: ({ documentDate }) =>
          dayjs(documentDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'counterparts',
        width: 300,
        title: t('counterparts.provider'),
        dataIndex: ['supplier', 'name']
      },
      {
        key: 'beneficiary',
        title: t('counterparts.purchase_center'),
        dataIndex: ['beneficiary', 'name']
      },
      {
        key: 'inquiryItems',
        title: t('stock_applications.consumables'),
        render: ({ items }: { items: InventoryDocumentItemResponse[] }) => {
          if (!items?.length) return '-'

          const extraCount = items?.length - VISIBLE_TAG_COUNT

          return (
            <Flex>
              <List
                itemLayout="horizontal"
                dataSource={items}
                renderItem={(item, index) =>
                  index < VISIBLE_TAG_COUNT && (
                    <Tag
                      key={item?.id}
                    >{`${item?.name} (${item?.quantity})`}</Tag>
                  )
                }
              />
              <Popover
                content={
                  <Flex vertical gap={4}>
                    {items?.slice(VISIBLE_TAG_COUNT).map((item) => (
                      <Tag
                        key={item?.id}
                      >{`${item?.name} (${item?.quantity})`}</Tag>
                    ))}
                  </Flex>
                }
              >
                {extraCount > 0 && <Tag className="pointer">+{extraCount}</Tag>}
              </Popover>
            </Flex>
          )
        }
      },
      {
        key: 'state',
        title: t('form.status'),
        render: ({ state }) => {
          return (
            <Tag color={getStatus(t, state).color} key={state}>
              {getStatus(t, state).text}
            </Tag>
          )
        }
      },
      {
        key: 'details',
        align: 'center',
        width: 103,
        render: ({ id }) => {
          return (
            <Link to={paths.issuance.issuanceDetails(id)}>
              {t('common.details')}
            </Link>
          )
        }
      }
    ],
    [t]
  )

  return (
    <PageBreadcrumbs
      title={t('menu.issuance')}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('menu.stock') }
      ]}
    >
      <Flex gap={16}>
        <IssuanceFilters
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />

        <Button
          type="primary"
          onClick={() => navigate(paths.stockAdmin.issuanceCreate)}
        >
          {t('stock_administration.add_issuance')}
        </Button>
      </Flex>

      <Divider />

      <Flex vertical gap={24}>
        <Table
          rowKey="id"
          columns={columns}
          loading={isLoading}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
